import { API_BASE_URL } from '../../config/api';
import apiCache, { CACHE_TTL } from '../../lib/apiCache';

const amaInsKey = (amacodigo, gira = null) =>
  gira != null && gira !== '' ? `amaIns:${amacodigo}:${gira}` : `amaIns:${amacodigo}`;

/** Campos que usa el API; evita enviar amateurData completo (SELECT * puede traer binarios enormes). */
function slimSavePayload(raw) {
  const d = raw && typeof raw === 'object' ? raw : {};
  const gira = d.giracodigo ?? d.giraCodigo;
  const out = {
    giracodigo: gira != null ? parseInt(String(gira), 10) : NaN,
    email: typeof d.email === 'string' ? d.email.trim().slice(0, 320) : '',
    etapa: d.etapa != null ? parseInt(String(d.etapa), 10) || 0 : 0,
  };
  if (d.amateurCodigo != null && String(d.amateurCodigo).trim() !== '') {
    const n = parseInt(String(d.amateurCodigo), 10);
    if (!Number.isNaN(n)) out.amateurCodigo = n;
  }
  if (d.amaInsImporte != null && d.amaInsImporte !== '') {
    const v = Number(d.amaInsImporte);
    if (Number.isFinite(v) && v > 0) out.amaInsImporte = v;
  }
  if (d.referencia != null && String(d.referencia).trim() !== '') {
    out.referencia = String(d.referencia).trim().slice(0, 24);
  }
  if (typeof d.amaInsTicketText === 'string' && d.amaInsTicketText.trim()) {
    out.amaInsTicketText = d.amaInsTicketText.trim().slice(0, 50);
  }
  return out;
}

const GiraAmaIns = {
  /**
   * Obtiene las inscripciones del amateur a gira(s).
   * @param {number} amacodigo - AmateurCodigo del jugador
   * @param {number} [gira] - opcional: filtrar por gira
   * @returns {Promise<{ inscripciones: Array }>}
   */
  fetch: async (amacodigo, gira = null) => {
    const key = amaInsKey(amacodigo, gira);
    const cached = apiCache.get(key);
    if (cached !== undefined) return cached;

    const params = new URLSearchParams({ amacodigo: String(amacodigo) });
    if (gira != null && gira !== '') params.set('gira', String(gira));
    const response = await fetch(`${API_BASE_URL}/api/amateurinscripcion?${params.toString()}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Error al obtener inscripciones');
    }
    return apiCache.set(key, data.inscripciones || [], CACHE_TTL.INS);
  },

  /**
   * Marca la inscripción amateur como impresa (AmaInsMarcaImpreso = 1).
   * @param {number} amaInsNumero - AmaInsNumero de la inscripción
   * @returns {Promise<{ message, rowsAffected }>}
   */
  markImpreso: async (amaInsNumero) => {
    const response = await fetch(`${API_BASE_URL}/api/amateurinscripcion/marcaimpreso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amaInsNumero }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Error al marcar inscripción');
    }
    return data;
  },

  /**
   * Guarda inscripción(es) a gira amateur.
   * @param {Object} inscriptionData - { giracodigo, email, amateurCodigo?, etapa?, amaInsImporte?, ... }
   * @returns {Promise<{ message, inscripciones, rowsAffected }>}
   */
  save: async (inscriptionData) => {
    const response = await fetch(`${API_BASE_URL}/api/saveGiraInscripcionAmateur`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(slimSavePayload(inscriptionData)),
    });

    const contentType = response.headers.get('content-type');
    const text = await response.text();
    let data = { message: null };
    if (contentType && contentType.includes('application/json') && text) {
      try {
        data = JSON.parse(text);
      } catch {
        // Server returned HTML (e.g. 413 error page) but wrong Content-Type — avoid "Unexpected token '<'" 
        if (response.status === 413) {
          throw new Error('Los datos enviados son demasiado grandes. Intenta de nuevo o contacta soporte.');
        }
        throw new Error('Error inesperado del servidor. Intenta de nuevo.');
      }
    } else if (response.status === 413) {
      throw new Error('Los datos enviados son demasiado grandes. Intenta de nuevo o contacta soporte.');
    } else if (!response.ok && text.startsWith('<')) {
      throw new Error('Error del servidor. Intenta de nuevo o contacta soporte.');
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || (response.status === 413 ? 'Los datos enviados son demasiado grandes.' : 'Error al guardar la inscripción amateur'));
    }

    apiCache.invalidate('amaIns:');
    apiCache.invalidate('giraAmateur');
    return data;
  },
};

export default GiraAmaIns;
